import { World, Vec2, Box, Circle, Polygon } from "planck";
import type { Vec2 as PlanckVec2 } from "planck";
import type { BenchScene } from "./scenes";
import type { EngineRunner } from "./runner";

// Planck.js (a Box2D port) works in meters, like Fast2D — no scale conversion.
// Gravity is +y to match the y-down convention shared across our runners.

export function createPlanckRunner(scene: BenchScene): EngineRunner {
	const world = new World({ gravity: new Vec2(0, scene.gravityY), allowSleep: false });
	let count = 0;

	scene.build((s) => {
		const body = world.createBody({
			type: s.isStatic ? "static" : "dynamic",
			position: new Vec2(s.x, s.y),
		});
		const opt = {
			density: 1,
			friction: s.friction ?? 0.4,
			restitution: s.restitution ?? 0,
		};
		if (s.kind === "circle") {
			body.createFixture(new Circle(s.r!), opt);
		} else if (s.kind === "triangle") {
			body.createFixture(new Polygon(s.verts!.map((v) => new Vec2(v[0], v[1]))), opt);
		} else {
			body.createFixture(new Box(s.w! / 2, s.h! / 2), opt);
		}
		count++;
	});

	return {
		name: "Planck.js",
		get bodyCount() {
			return count;
		},
		step(dt: number): void {
			world.step(dt);
		},
		forEachBody(cb): void {
			for (let b = world.getBodyList(); b; b = b.getNext()) {
				const fixture = b.getFixtureList();
				if (!fixture) continue;
				const pos = b.getPosition();
				const angle = b.getAngle();
				const isStatic = b.getType() === "static";
				const shape = fixture.getShape();
				if (shape.getType() === "circle") {
					cb({
						kind: "circle",
						x: pos.x,
						y: pos.y,
						r: shape.getRadius(),
						angle,
						isStatic,
					});
				} else {
					const local = (shape as unknown as { m_vertices: PlanckVec2[] }).m_vertices;
					const verts: number[] = [];
					for (const v of local) {
						const wp = b.getWorldPoint(v);
						verts.push(wp.x, wp.y);
					}
					cb({ kind: "poly", verts, isStatic });
				}
			}
		},
		dispose(): void {
			for (let body = world.getBodyList(); body; ) {
				const next = body.getNext();
				world.destroyBody(body);
				body = next;
			}
		},
	};
}
